import { type FC, type ReactNode } from 'react';
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@highereducation/hen-ui';
import useMediaQuery from '@de/hooks/useMediaQuery';

/**
 * Hidden scrollbar + bottom-sheet sizing overrides for the governed
 * lead-capture drawer. Owned here so the DSL-driven governed surface
 * (`LeadFlowProvider`) and the imperative `ResultsPage` render an
 * identical drawer chrome from a single source. The
 * `data-[vaul-drawer-direction=bottom]` overrides let the mobile bottom
 * sheet span the full viewport width.
 */
const DRAWER_CONTENT_STYLES =
  'bg-neutral-50 overflow-x-hidden overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden data-[vaul-drawer-direction=bottom]:!max-h-full data-[vaul-drawer-direction=bottom]:!mt-0 data-[vaul-drawer-direction=bottom]:w-full data-[vaul-drawer-direction=bottom]:max-w-full';

export interface SingleSubmissionDrawerProps {
  /** Whether the slideout is open. */
  readonly open: boolean;
  /** Invoked when the drawer requests a close (overlay click / Escape). */
  readonly onClose: () => void;
  /**
   * Drawer body — typically a `<SingleProgramSubmission>` gated on the
   * selected result. Callers own the body so the differing lead /
   * results prop wiring stays at each call site.
   */
  readonly children: ReactNode;
  /** Screen-reader-only title. Defaults to "Program submission". */
  readonly title?: string;
}

/**
 * Shared slideout chrome for the governed single-program submission
 * surface. Below the `md` breakpoint (767px) it renders a full-height
 * bottom sheet; at `md`+ it renders a right-side half-screen drawer —
 * matching the Tailwind `md` breakpoint in hen-ui/tailwind.config.ts.
 *
 * Extracted so the DSL render path (`LeadFlowProvider`) and the
 * imperative `ResultsPage` (`variants/Default.tsx`) share one drawer
 * implementation instead of two copy-adapted JSX blocks.
 */
const SingleSubmissionDrawer: FC<SingleSubmissionDrawerProps> = ({
  open,
  onClose,
  children,
  title = 'Program submission',
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  return (
    <Drawer
      direction={isMobile ? 'bottom' : 'right'}
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DrawerContent
        size={isMobile ? 'full' : '1/2'}
        className={DRAWER_CONTENT_STYLES}
        bottomBarEnd={
          isMobile ? (
            <DrawerClose asChild>
              <Button
                type='button'
                variant='iconOnly'
                size='sm'
                iconBefore='x'
                aria-label='Close program details'
                className='shrink-0'
              />
            </DrawerClose>
          ) : undefined
        }
      >
        <DrawerHeader className='sr-only'>
          <DrawerTitle>{title}</DrawerTitle>
          <DrawerDescription>
            Review program details and confirm your contact information.
          </DrawerDescription>
        </DrawerHeader>
        {children}
      </DrawerContent>
    </Drawer>
  );
};

export default SingleSubmissionDrawer;
